import { Measure } from '../types'
import Big from 'big.js'

enum AngleMeasurementSystem {
  siUnits = 'SI Units',
  commonUnits = 'Common Units',
  specializedUnits = 'Specialized Units',
  astrologicalUnit = 'Astrological Units',
  fractionalCircleUnits = 'Fractional Circle Units',
  fullCircleUnits = 'Full Circle Units',
}


const angleUnits = {
  degree: {
    displayName: 'Degree',
    codeName: 'degree',
    url: 'degree',
    symbol: '°',
    toBaseUnit: (value: Big): Big => value,
    fromBaseUnit: (value: Big): Big => value,
    system: AngleMeasurementSystem.commonUnits
  },
  second: {
    displayName: 'Second',
    codeName: 'second',
    url: 'second',
    symbol: '″',
    toBaseUnit: (value: Big): Big => value.times(1 / 3600),
    fromBaseUnit: (value: Big): Big => value.times(3600),
    system: AngleMeasurementSystem.commonUnits
  },
  minute: {
    displayName: 'Minute',
    codeName: 'minute',
    url: 'minute',
    symbol: '′',
    toBaseUnit: (value: Big): Big => value.times(1 / 60),
    fromBaseUnit: (value: Big): Big => value.times(60),
    system: AngleMeasurementSystem.commonUnits
  },

  radian: {
    displayName: 'Radian',
    codeName: 'radian',
    url: 'radian',
    symbol: 'rad',
    toBaseUnit: (value: Big): Big => value.times(180).div(Math.PI),
    fromBaseUnit: (value: Big): Big => value.times(Math.PI).div(180),
    system: AngleMeasurementSystem.siUnits
  },

  quadrant: {
    displayName: 'Quadrant',
    codeName: 'quadrant',
    url: 'quadrant',
    symbol: 'quad',
    toBaseUnit: (value: Big): Big => value.times(90),
    fromBaseUnit: (value: Big): Big => value.div(90),
    system: AngleMeasurementSystem.fractionalCircleUnits
  },
  right_angle: {
    displayName: 'Right Angle',
    codeName: 'right_angle',
    url: 'right-angle',
    symbol: 'RA',
    toBaseUnit: (value: Big): Big => value.times(90),
    fromBaseUnit: (value: Big): Big => value.div(90),
    system: AngleMeasurementSystem.fractionalCircleUnits
  },

  circle: {
    displayName: 'Circle',
    codeName: 'circle',
    url: 'circle',
    symbol: 'cir',
    toBaseUnit: (value: Big): Big => value.times(360),
    fromBaseUnit: (value: Big): Big => value.div(360),
    system: AngleMeasurementSystem.fullCircleUnits
  },
  revolution: {
    displayName: 'Revolution',
    codeName: 'revolution',
    url: 'revolution',
    symbol: 'rev',
    toBaseUnit: (value: Big): Big => value.times(360),
    fromBaseUnit: (value: Big): Big => value.div(360),
    system: AngleMeasurementSystem.fullCircleUnits
  },
  turn: {
    displayName: 'Turn',
    codeName: 'turn',
    url: 'turn',
    symbol: 'turn',
    toBaseUnit: (value: Big): Big => value.times(360),
    fromBaseUnit: (value: Big): Big => value.div(360),
    system: AngleMeasurementSystem.fullCircleUnits
  },


  grad: {
    displayName: 'Grad (Gon)',
    codeName: 'grad',
    url: 'grad',
    symbol: 'grad',
    toBaseUnit: (value: Big): Big => value.times(0.9),
    fromBaseUnit: (value: Big): Big => value.div(0.9),
    system: AngleMeasurementSystem.specializedUnits
  },
  mil: {
    displayName: 'Mil',
    codeName: 'mil',
    url: 'mil',
    symbol: 'mil',
    toBaseUnit: (value: Big): Big => value.times(0.05625),
    fromBaseUnit: (value: Big): Big => value.div(0.05625),
    system: AngleMeasurementSystem.specializedUnits
  },
  sextant: {
    displayName: 'Sextant',
    codeName: 'sextant',
    url: 'sextant',
    symbol: 'sext',
    toBaseUnit: (value: Big): Big => value.times(60),
    fromBaseUnit: (value: Big): Big => value.div(60),
    system: AngleMeasurementSystem.specializedUnits
  },


  sign: {
    displayName: 'Sign',
    codeName: 'sign',
    url: 'sign',
    symbol: 'sign',
    toBaseUnit: (value: Big): Big => value.times(30),
    fromBaseUnit: (value: Big): Big => value.div(30),
    system: AngleMeasurementSystem.astrologicalUnit
  }
}

export const angle: Measure = {
  displayName: 'Angle',
  codeName: 'angle',
  url: 'angle',
  units: angleUnits,
  baseUnit: angleUnits.degree,
  defaultFromUnit: angleUnits.circle,
  defaultToUnit: angleUnits.degree
}
